/**
 * Período de datas dos filtros (feed, ramo, incidentes).
 *
 * Tudo em DIA CIVIL de São Paulo (`YYYY-MM-DD`): "hoje" é o dia de lá, não o do
 * fuso do navegador, para a tela e a API concordarem sobre o que é "hoje".
 */

export const FUSO = 'America/Sao_Paulo';

const formatoDia = new Intl.DateTimeFormat('en-CA', { timeZone: FUSO, year: 'numeric', month: '2-digit', day: '2-digit' });
const REGEX_DIA = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Dia civil de São Paulo (YYYY-MM-DD) de um instante. Vazio se inválido. */
export function diaCivilSP(valor: Date | string): string {
  const data = typeof valor === 'string' ? new Date(valor) : valor;
  if (Number.isNaN(data.getTime())) return '';
  return formatoDia.format(data);
}

export function diaValido(valor: string | null | undefined): valor is string {
  if (!valor) return false;
  const m = REGEX_DIA.exec(valor);
  if (!m) return false;
  const data = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return data.getUTCMonth() === Number(m[2]) - 1 && data.getUTCDate() === Number(m[3]);
}

export function somarDias(dia: string, dias: number): string {
  const m = REGEX_DIA.exec(dia)!;
  const data = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + dias));
  return data.toISOString().slice(0, 10);
}

export type PresetPeriodo = 'hoje' | '7d' | '30d' | 'mes';

export const PRESETS_PERIODO: Array<{ id: PresetPeriodo; rotulo: string }> = [
  { id: 'hoje', rotulo: 'Hoje' },
  { id: '7d', rotulo: 'Últimos 7 dias' },
  { id: '30d', rotulo: 'Últimos 30 dias' },
  { id: 'mes', rotulo: 'Este mês' },
];

export interface Periodo {
  de: string | null;
  ate: string | null;
}

export function intervaloDoPreset(id: PresetPeriodo, agora: Date = new Date()): { de: string; ate: string } {
  const hoje = diaCivilSP(agora);
  switch (id) {
    case 'hoje':
      return { de: hoje, ate: hoje };
    case '7d':
      return { de: somarDias(hoje, -6), ate: hoje };
    case '30d':
      return { de: somarDias(hoje, -29), ate: hoje };
    case 'mes':
      return { de: `${hoje.slice(0, 8)}01`, ate: hoje };
  }
}

/** Qual preset corresponde exatamente ao intervalo (ou `null` = personalizado). */
export function presetAtivo(de: string | null, ate: string | null, agora: Date = new Date()): PresetPeriodo | null {
  if (!de || !ate) return null;
  return PRESETS_PERIODO.find((p) => {
    const i = intervaloDoPreset(p.id, agora);
    return i.de === de && i.ate === ate;
  })?.id ?? null;
}

/** Mensagem de erro do par De/Até, ou `null` se válido (campos vazios são válidos: intervalo aberto). */
export function validarPeriodo(de: string | null | undefined, ate: string | null | undefined): string | null {
  if (de && !diaValido(de)) return 'A data inicial (De) é inválida.';
  if (ate && !diaValido(ate)) return 'A data final (Até) é inválida.';
  if (de && ate && de > ate) return 'A data inicial (De) não pode ser depois da data final (Até).';
  return null;
}

function br(dia: string): string {
  const [a, m, d] = dia.split('-');
  return `${d}/${m}/${a}`;
}

/** "Últimos 7 dias", "01/10/2026 a 23/10/2026", "a partir de 01/10/2026", "até 23/10/2026". */
export function rotuloPeriodo(de: string | null, ate: string | null, agora: Date = new Date()): string {
  const preset = presetAtivo(de, ate, agora);
  if (preset) return PRESETS_PERIODO.find((p) => p.id === preset)!.rotulo;
  if (de && ate) return de === ate ? br(de) : `${br(de)} a ${br(ate)}`;
  if (de) return `a partir de ${br(de)}`;
  if (ate) return `até ${br(ate)}`;
  return 'Qualquer período';
}

/** Lê `de`/`ate` da URL, descartando valor inválido ou invertido. */
export function periodoDaUrl(params: URLSearchParams): Periodo {
  const de = params.get('de');
  const ate = params.get('ate');
  const periodo = { de: diaValido(de) ? de : null, ate: diaValido(ate) ? ate : null };
  return validarPeriodo(periodo.de, periodo.ate) ? { de: null, ate: null } : periodo;
}
